// /app/frontend/src/pages/SavedConfigsPage.jsx
// User's saved shade configurations with one-click load

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bookmark, Trash2, Loader2, ArrowLeft, Plus, Edit2, 
  Package, ChevronRight, Calendar, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { resolveImageUrl } from '../lib/imageCache';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SavedConfigsPage = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchConfigs = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/user/saved-configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Failed to fetch saved configs:', error);
      toast.error('Failed to load saved configurations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/saved-configs');
      return;
    }
    fetchConfigs();
  }, [authLoading, isAuthenticated, navigate, fetchConfigs]);

  const handleDelete = async (configId) => {
    setDeletingId(configId);
    try {
      await axios.delete(`${API_URL}/api/user/saved-configs/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(prev => prev.filter(c => c.id !== configId));
      toast.success('Configuration deleted');
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast.error('Failed to delete configuration');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setEditName(config.name);
  };

  const handleSaveEdit = async () => {
    if (!editingConfig || !editName.trim()) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/user/saved-configs/${editingConfig.id}`,
        {
          name: editName.trim(),
          config: editingConfig.config,
          product_type: editingConfig.product_type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConfigs(prev => prev.map(c => 
        c.id === editingConfig.id ? { ...c, name: editName.trim() } : c
      ));
      setEditingConfig(null);
      toast.success('Configuration renamed');
    } catch (error) {
      console.error('Failed to update config:', error);
      toast.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = (config) => {
    // Store config in sessionStorage for the product page to pick up
    sessionStorage.setItem('loadSavedConfig', JSON.stringify(config.config));
    
    // Navigate to the product configurator
    const productType = config.product_type || 'roller-shades';
    navigate(`/products/${productType}?loadConfig=true`);
    toast.success(`Loading "${config.name}" configuration...`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-600" />
            <p className="mt-4 text-neutral-500">Loading saved configurations...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="py-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Bookmark className="w-8 h-8 text-amber-600" />
                  <h1 className="text-3xl font-bold text-neutral-900">Saved Configurations</h1>
                </div>
                <p className="text-neutral-500">
                  Your saved shade presets. Load any configuration with one click.
                </p>
              </div>
              <Button
                onClick={() => navigate('/products/roller-shades')}
                className="bg-amber-600 hover:bg-amber-700 gap-2"
              >
                <Plus className="w-4 h-4" />
                New Configuration
              </Button>
            </div>
          </div>

          {/* Configs List */}
          <div className="space-y-4">
            {configs.length === 0 ? (
              <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
                <Bookmark className="w-16 h-16 mx-auto text-neutral-200 mb-4" />
                <h2 className="text-xl font-semibold text-neutral-700 mb-2">
                  No Saved Configurations
                </h2>
                <p className="text-neutral-500 mb-6">
                  Save your shade configurations as presets for quick access later.
                </p>
                <Button
                  onClick={() => navigate('/products/roller-shades')}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Create Your First Configuration
                </Button>
              </div>
            ) : (
              <AnimatePresence>
                {configs.map((config, index) => (
                  <motion.div
                    key={config.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="p-4 flex items-center gap-4">
                      {/* Color Swatch Preview */}
                      <div
                        className="w-16 h-16 rounded-lg border border-neutral-200 flex-shrink-0"
                        style={{ 
                          backgroundColor: config.config?.fabric_color_hex || '#E5E5E5' 
                        }}
                      >
                        {config.config?.fabric_image_url && (
                          <img
                            src={resolveImageUrl(config.config.fabric_image_url, 256)}
                            alt={config.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        )}
                      </div>
                      
                      {/* Config Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-neutral-900 truncate">{config.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-neutral-500 mt-1">
                          <span>{config.config?.width || '?'}" × {config.config?.height || '?'}"</span>
                          <ChevronRight className="w-3 h-3" />
                          <span>{config.config?.fabric_color_name || 'Custom'}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-neutral-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {config.config?.mount_type || 'inside'} mount
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(config.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(config)}
                          className="gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(config.id)}
                          disabled={deletingId === config.id}
                          className="text-red-600 hover:bg-red-50 gap-1"
                        >
                          {deletingId === config.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleLoad(config)}
                          className="bg-amber-600 hover:bg-amber-700 gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Load
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>
      </main>
      
      <Footer />

      {/* Edit Name Dialog */}
      <Dialog open={!!editingConfig} onOpenChange={() => setEditingConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Configuration</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Configuration name"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConfig(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving || !editName.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedConfigsPage;
